import { useState } from "react";
import { Plus } from "lucide-react";
import { trpc } from "../../hooks/trpc";
import { LabelLine } from "../brand";
import { useAuth } from "../../hooks/useAuth";
import { isGfRole } from "../../../../shared/permissions";
import { createRoleInputSchema, ROLE_KEY_MAX, ROLE_LABEL_MAX } from "../../../../shared/roles";
import { INSTANCE } from "../../lib/instance";
import { isCoreInstance } from "../../../../shared/instance";

// =====================================================================
// Neue individuelle Rolle anlegen (Migration 0053), nur GF.
// Die Rolle startet ohne App-Freigabe; Zugriffe werden danach in der
// Matrix darunter gesetzt. Server validiert dasselbe Schema erneut und
// prueft Dubletten, das hier ist nur schnelle Rueckmeldung.
// =====================================================================

export function RoleCreateForm({ onCreated }: { onCreated?: (role: { id: number; key: string; label: string }) => void }) {
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const [label, setLabel] = useState("");
  const [key, setKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  const privateProfile = isCoreInstance(INSTANCE);

  const createRole = trpc.admin.rbacCreateRole.useMutation({
    onSuccess: async (role) => {
      setCreated(role.key);
      setLabel("");
      setKey("");
      await Promise.all([utils.admin.rbacMatrix.invalidate(), utils.admin.listRoles.invalidate()]);
      onCreated?.(role);
    },
    onError: (err) => setError(err.message),
  });

  if (!isGfRole(user?.roleName, INSTANCE, user?.roleScope)) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setCreated(null);
    const parsed = createRoleInputSchema.safeParse({ key, label });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Eingabe ungültig");
      return;
    }
    createRole.mutate(parsed.data);
  };

  return (
    <form onSubmit={handleSubmit} aria-label="Neue Rolle anlegen" className="mb-6 pb-5" style={{ borderBottom: "1px solid var(--color-border)" }}>
      <h4 className="text-xs font-semibold mb-1" style={{ fontFamily: "var(--font-display)", color: "var(--color-foreground)" }}>
        Neue Rolle
      </h4>
      <p className="text-xs mb-3" style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-body)" }}>
        Startet ohne App-Zugriff. Freigaben danach in der Matrix setzen. Der Schlüssel ist dauerhaft und kann später nicht geändert werden.
      </p>
      <div className="flex flex-col md:flex-row gap-3 md:items-end">
        <div className="flex-1">
          <LabelLine as="label" htmlFor="role-create-label" style={{ marginBottom: 8, display: "block" }}>Anzeigename</LabelLine>
          <input
            id="role-create-label"
            className="input mt-2"
            value={label}
            maxLength={ROLE_LABEL_MAX}
            onChange={e => setLabel(e.target.value)}
            placeholder={privateProfile ? "z. B. Familie" : "z. B. Bauleitung Nord"}
          />
        </div>
        <div className="md:w-56">
          <LabelLine as="label" htmlFor="role-create-key" style={{ marginBottom: 8, display: "block" }}>Schlüssel</LabelLine>
          <input
            id="role-create-key"
            className="input mt-2"
            value={key}
            maxLength={ROLE_KEY_MAX}
            onChange={e => setKey(e.target.value)}
            placeholder={privateProfile ? "z. B. Familie" : "z. B. BL-Nord"}
            style={{ fontFamily: "var(--font-mono)" }}
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={createRole.isPending}>
          <Plus size={14} strokeWidth={1.5} /> Rolle anlegen
        </button>
      </div>
      <p className="text-[10px] mt-2" style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}>
        Schlüssel: 2 bis {ROLE_KEY_MAX} Zeichen, Buchstaben, Ziffern, einzelne Bindestriche, Beginn mit Buchstabe. Systemrollen sind reserviert.
      </p>
      {error && (
        <p role="alert" className="text-xs mt-2" style={{ color: "var(--color-danger)", fontFamily: "var(--font-mono)" }}>
          {error}
        </p>
      )}
      {created && (
        <p className="text-xs mt-2" style={{ color: "var(--color-success)", fontFamily: "var(--font-mono)" }}>
          Rolle {created} angelegt, noch ohne App-Zugriff.
        </p>
      )}
    </form>
  );
}
